/**
 * Confirmation dialog before CIG note / CPT / document save flows.
 * @module updateCigNoteConfirmationDialog
 */

import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { ModalId } from "../../../enums/modalId";
import WarningIcon from "../../../svg/warningIcon";
import { useLinkedOrdersModalView } from "../../expandableTableForOrderAuth/hooks/useLinkedOrdersModalView";
import { SingleOrderClubbedData } from "../../expandableTableForOrderAuth/types";
import {
  getUpdateCigNoteConfirmationDialogTitle,
  UpdateCigNoteConfirmationSource,
} from "./getUpdateCigNoteConfirmationDialogTitle";
import RenderSiblingOrderContentBody from "./renderSiblingOrderContentBody";

/**
 * Optional linked-orders state override for scoped confirmation flows
 * (e.g. CPT save filtering siblings by date of service).
 */
export interface LinkedOrdersConfirmationViewState {
  /** Sibling rows rendered in the confirmation expandable table. */
  expandableTableData: SingleOrderClubbedData[];
  /** Error from the sibling-orders fetch, when the table cannot load. */
  siblingOrdersError: string | null;
  /** Pre-selected sibling order ids passed to `onSave` on confirm. */
  selectedSiblingOrderIds: string[];
}

/** Props for {@link UpdateCigNoteConfirmationDialog}. */
interface UpdateCigNoteConfirmationDialogProps {
  /** Confirms save; receives selected linked order ids when the table is shown. */
  onSave: (selectedOrderIds?: string[]) => void;
  onClose: () => void;
  onCancel: () => void;
  loading: boolean;
  source: UpdateCigNoteConfirmationSource;
  /** When set, replaces context-derived linked-order rows and selection. */
  linkedOrdersView?: LinkedOrdersConfirmationViewState;
}

/**
 * Gates save flows with legacy copy or a linked sibling orders table.
 *
 * @param props - Save/close handlers, loading flag, and confirmation source
 * @returns Update confirmation modal
 */
const UpdateCigNoteConfirmationDialog = (
  props: UpdateCigNoteConfirmationDialogProps,
) => {
  const defaultLinkedOrdersView = useLinkedOrdersModalView();
  const linkedOrdersView = props.linkedOrdersView ?? {
    expandableTableData: defaultLinkedOrdersView.expandableTableData,
    siblingOrdersError: defaultLinkedOrdersView.siblingOrdersError,
    selectedSiblingOrderIds: defaultLinkedOrdersView.selectedSiblingOrderIds,
  };
  const { expandableTableData, siblingOrdersError, selectedSiblingOrderIds } =
    linkedOrdersView;

  const handleSave = () => {
    props.onSave(
      expandableTableData.length > 0 && selectedSiblingOrderIds.length > 0
        ? selectedSiblingOrderIds
        : undefined,
    );
  };

  return (
    <Modal
      dialogId={ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG}
      title={getUpdateCigNoteConfirmationDialogTitle(
        props.source,
        expandableTableData.length,
      )}
      onSave={handleSave}
      onClose={props.onClose}
      onCancel={props.onCancel}
      saveButtonText={props.loading ? "Updating..." : "Yes, Confirm"}
      cancelText="No, Cancel"
      disableSave={props.loading}
      maxHeightPercentage={80}
    >
      <div className="update-confirmation--conatiner flex flex-col items-center justify-center gap-3 p-4">
        {expandableTableData.length > 0 ? (
          <RenderSiblingOrderContentBody
            expandedContentType="table"
            expandableTableData={expandableTableData}
            siblingOrdersLoading={false}
            siblingOrdersError={siblingOrdersError}
          />
        ) : (
          <>
            <div className="warning-icon">
              <WarningIcon />
            </div>
            <div className="warning-message text-center text-body font-normal text-primaryGray-1">
              {props.source === "j_codes"
                ? "Are your sure you want to update details for the J-Codes ?"
                : props.source === "cig_notes"
                  ? "Are your sure you want to write back the notes and update the  radio buttons ?"
                  : props.source === "cpt_codes"
                    ? "Are your sure you want to update the CPT codes ?"
                    : "Are your sure you want to upload the documents ?"}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default UpdateCigNoteConfirmationDialog;
